/**
 * @file: Dashboard.jsx
 * @status: Sovereign Bridge (V12.6.1) - COMPACT & GLOBAL ACTIVE & ACCESSIBILITY ENHANCED
 * @authority: @CEO - Abdullah Ahmed Al-Zahrani
 * @version: 2026.04.27
 * @description: تحديث الرؤية الكاملة 100%؛ إضافة سمات id/name للأزرار التفاعلية؛ دمج i18n مع بروتوكول POST بختم @CEO.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  TrendingUp, GraduationCap, Brain, Microscope, Activity, 
  Eye, ChevronRight, Lock, ShieldCheck,
  Landmark, Cpu, Fingerprint, Rocket
} from 'lucide-react';
import { message, Tag, Button } from 'antd';

const Dashboard = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [patentCount, setPatentCount] = useState('...'); 

  const CLOUD_API_URL = "https://portal.huger.sa/api.php"; 
  const userRole = localStorage.getItem('huger_user_role');
  const userName = localStorage.getItem('huger_user_name');
  const isAuthenticated = localStorage.getItem('huger_authenticated') === 'true';

  useEffect(() => {
    if (!isAuthenticated) {
      message.warning("بروتوكول أمني: يرجى إثبات الهوية للوصول للرادار السيادي.");
      navigate('/access-gate');
      return;
    }

    const fetchStats = async () => {
      try {
        const response = await axios.post(CLOUD_API_URL, { action: 'p_radar' });
        const count = Array.isArray(response.data) ? response.data.length : 0;
        setPatentCount(count.toLocaleString());
      } catch (error) {
        console.error("❌ عائق في الجسر السحابي:", error);
        setPatentCount('0');
      }
    };
    fetchStats();
  }, [isAuthenticated, navigate]);

  const kpis = [
    { 
      label: t('industrial_loan'), 
      value: (userRole === 'CEO' || userRole === 'Investor') ? '24.28M' : 'SECURED', 
      sub: 'SAR - V-SYNC', 
      icon: <Landmark size={20} />, 
      color: 'text-emerald-400',
      locked: userRole === 'Staff'
    },
    { 
      label: t('investor_gate'), 
      value: (userRole === 'CEO' || userRole === 'Investor') ? '0.0%' : 'ENCRYPTED', 
      sub: i18n.language === 'ar' ? 'انتظار التشغيل' : 'Awaiting Launch', 
      icon: <TrendingUp size={20} />, 
      color: 'text-blue-400',
      locked: userRole === 'Staff'
    },
    { 
      label: i18n.language === 'ar' ? 'كفاءة مصنع الطائف' : 'Taif Complex Efficiency', 
      value: '0.0%', 
      sub: i18n.language === 'ar' ? 'خامل - إعداد' : 'Idle - Setup', 
      icon: <Cpu size={20} />, 
      color: 'text-slate-500',
      locked: false
    },
  ];

  const impactStats = [
    { label: t('impact_students'), value: '0', icon: <GraduationCap size={18} className="text-slate-600" /> },
    { label: t('impact_patents'), value: patentCount, icon: <ShieldCheck size={18} className="text-emerald-400" /> },
    { label: t('innovation_hours'), value: '0', icon: <Brain size={18} className="text-slate-600" /> },
    { label: t('scientific_trials'), value: '0', icon: <Microscope size={18} className="text-slate-600" /> }
  ];

  return (
    <div className={`p-4 md:p-6 max-w-7xl mx-auto bg-[#050B14] text-white rounded-[2.5rem] shadow-2xl border border-white/5 my-4 relative overflow-hidden ${i18n.language === 'ar' ? 'text-right' : 'text-left'}`} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      
      <div className="absolute top-0 right-0 w-[40%] h-48 bg-gradient-to-br from-[#F57C00]/5 to-transparent blur-[80px] pointer-events-none"></div>

      {/* القسم القيادي العلوي - Compact */}
      <div className="mb-8 flex flex-col lg:flex-row justify-between items-start gap-6 relative z-10 scale-95 origin-top">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
             <Tag className="bg-gradient-to-r from-[#F57C00] to-orange-700 text-white font-black rounded-full px-4 py-0.5 border-none shadow-lg uppercase italic text-[8px]">
               {t('sovereign_status')}: {userRole}
             </Tag>
             <span className="text-slate-500 font-black text-[9px] uppercase italic opacity-60 tracking-wider">ID: {userName}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black mb-3 tracking-tighter italic leading-none uppercase">
            {i18n.language === 'ar' ? 'رادار' : 'RADAR'} <span className="text-[#F57C00]">{i18n.language === 'ar' ? 'السيادة' : 'SOVEREIGNTY'}</span>
          </h1>
          <p className={`text-slate-400 font-bold text-xs leading-relaxed max-w-lg border-[#F57C00] py-1 italic ${i18n.language === 'ar' ? 'border-r-2 pr-4' : 'border-l-2 pl-4'}`}>
            {i18n.language === 'ar' 
              ? `بوابة التحكم المركزية لمجموعة ضخامة HUGER. تم بدء جرد الأصول من مجمع البيانات السحابي.`
              : `Central Command Gateway for HUGER. Initiating asset inventory from the Cloud Data Hub.`}
          </p>
        </div>
        <div className="hidden lg:flex flex-col items-center gap-2 opacity-30 group hover:opacity-100 transition-opacity">
           <div className="p-4 bg-black/40 rounded-2xl border border-white/10 shadow-inner">
              <Fingerprint size={40} className="text-slate-700 group-hover:text-[#F57C00] transition-colors" />
           </div>
           <p className="text-[7px] font-black text-slate-600 uppercase tracking-widest italic leading-none">Biometric Verified</p>
        </div>
      </div>

      {/* المؤشرات الاستراتيجية - Compact Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 relative z-10 scale-95 origin-top">
        {kpis.map((kpi, index) => (
          <div key={index} id={`kpi_card_${index}`} className="bg-gradient-to-br from-[#0A192F]/80 to-transparent border border-white/5 p-6 rounded-[2rem] shadow-xl hover:border-[#F57C00]/30 transition-all relative overflow-hidden backdrop-blur-xl">
            <div className="flex justify-between items-start mb-6">
              <div className={`p-3 bg-black/60 rounded-xl ${kpi.color} border border-white/5 shadow-inner`}>
                {kpi.locked ? <Lock size={18} className="text-slate-800"/> : kpi.icon}
              </div>
              <Activity size={14} className={`${kpi.locked ? 'text-slate-900' : 'text-emerald-500 animate-pulse'}`} />
            </div>
            <h3 className="text-slate-500 text-[9px] font-black uppercase tracking-widest mb-1 italic">{kpi.label}</h3>
            <span className={`text-3xl font-black italic tracking-tighter ${kpi.locked ? 'text-slate-800 blur-md select-none' : 'text-white'}`}>
              {kpi.value}
            </span>
            {kpi.locked && (
              <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center backdrop-blur-sm">
                 <Lock size={20} className="text-slate-700"/>
                 <span className="text-[8px] font-black text-slate-600 uppercase mt-1">Classified Access</span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* إحصائيات أثر STEAM - Compact Line */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10 relative z-10 scale-95 origin-top">
        {impactStats.map((stat, index) => (
          <div key={index} id={`impact_stat_${index}`} className="bg-[#0A192F]/40 border border-white/5 p-4 rounded-[1.5rem] hover:border-blue-500/40 transition-all shadow-lg backdrop-blur-xl">
            <div className="p-2 bg-black/40 rounded-lg border border-white/5 w-fit mb-3">
               {stat.icon}
            </div>
            <p className="text-[8px] font-black text-slate-500 uppercase tracking-tighter mb-1 leading-none h-4 overflow-hidden">{stat.label}</p>
            <h3 className="text-2xl font-black text-white italic tracking-tighter">{stat.value}</h3>
          </div>
        ))}

        <div 
          id="btn_navigate_showroom"
          name="showroom_action"
          onClick={() => navigate('/inventory')} 
          className="bg-gradient-to-br from-[#F57C00] to-orange-950 p-4 rounded-[1.5rem] flex flex-col justify-center items-center text-center shadow-2xl group cursor-pointer hover:scale-105 transition-all border border-white/10"
        >
          <Eye size={24} className="mb-2 text-white animate-pulse" />
          <h3 className="text-sm font-black uppercase italic tracking-tighter text-white leading-none">SHOWROOM</h3>
        </div>
      </div>

      {/* رادار الإنجاز والمهام - Compact Multi-column */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 relative z-10 scale-95 origin-top">
        <div id="infrastructure_radar" className="lg:col-span-2 bg-[#0A192F]/60 border border-white/5 p-8 rounded-[2.5rem] backdrop-blur-3xl relative shadow-2xl overflow-hidden">
          <h2 className="text-lg font-black mb-8 flex items-center gap-3 italic text-white uppercase tracking-tighter">
            <Rocket className="text-blue-500" size={20} /> {t('infrastructure')}
          </h2>
          <div className="space-y-8">
            <div>
              <div className="flex justify-between text-[9px] font-black mb-3 uppercase tracking-widest italic">
                <span className="text-slate-400">{t('localizing_ip')}</span>
                <span className="text-blue-400">0% INITIALIZING</span>
              </div>
              <div className="w-full bg-black/60 h-2 rounded-full overflow-hidden p-0.5 border border-white/10">
                <div className="bg-gradient-to-r from-blue-800 to-cyan-400 h-full rounded-full w-[0%] transition-all duration-[3s]"></div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-b from-[#0A192F] to-[#050B14] border border-white/5 p-8 rounded-[2.5rem] shadow-2xl flex flex-col group hover:border-[#F57C00]/20 transition-all">
          <h2 className="text-lg font-black mb-6 flex items-center gap-3 italic text-white uppercase tracking-tighter">
            <Activity className="text-[#F57C00]" size={20} /> {t('sovereign_tasks')}
          </h2>
          <div className="space-y-4 flex-1">
            {(userRole === 'CEO' || userRole === 'Manager') && (
              <div 
                id="task_loan_management"
                name="loan_protocol_action"
                onClick={() => navigate('/financing')} 
                className={`p-4 bg-orange-500/5 border-[#F57C00] rounded-xl hover:bg-orange-500/10 transition-all cursor-pointer shadow-md group/task ${i18n.language === 'ar' ? 'border-r-4 pr-4' : 'border-l-4 pl-4'}`}
              >
                <p className="text-xs font-black text-white italic group-hover/task:text-[#F57C00] transition-colors leading-none">{t('loan_management')}</p>
                <div className="mt-3 flex justify-between items-center text-[7px] font-black text-[#F57C00] uppercase tracking-widest leading-none">
                   <span>Active Protocol</span>
                   <ChevronRight size={14} className={`${i18n.language === 'en' ? 'rotate-180' : ''}`} />
                </div>
              </div>
            )}
          </div>
          {userRole === 'CEO' && (
            <button 
              id="btn_access_sovereign_control_room"
              name="control_room_action"
              onClick={() => navigate('/control-center')} 
              className="w-full mt-6 py-4 bg-white text-black hover:bg-[#F57C00] hover:text-white rounded-xl text-[9px] font-black uppercase tracking-widest transition-all shadow-xl active:scale-95 border-none cursor-pointer"
            >
              {t('control_room_btn')} <ShieldCheck size={14} className="inline mx-1" />
            </button>
          )}
        </div>
      </div>

      <footer className="mt-8 text-center relative z-10 border-t border-white/5 pt-6 pb-4 opacity-20 scale-90">
        <p className="text-gray-800 text-[8px] font-black uppercase tracking-[1em] italic leading-loose">
          HUGER SOVEREIGN ECOSYSTEM • v12.6.1 • 2026
        </p>
      </footer>
    </div>
  );
};

export default Dashboard;